/**
 * IntakeEventCard — carte d'une soumission publique reçue via un intake link
 * (Chunk 9.N.3.B — détail broker UI).
 *
 * Mode collapsed (défaut) :
 *   - date courte de réception
 *   - langue détectée si dispo (badge fr/en)
 *   - preview du commentaire (truncate ~80 chars)
 *   - count fichiers attachés (lazy via document-links query)
 *   - chevron toggle ▶
 *
 * Mode expanded (click ou Enter sur la carte) :
 *   - date complète locale
 *   - consent : « Accepté le {date} » (PAS le ip_hash, décision PO §7)
 *   - commentaire complet
 *   - email contact (mailto: si fourni)
 *   - source channel
 *   - liste fichiers via useEventDocumentLinks (lazy enabled=expanded) :
 *       file_name + size formatté + bouton Télécharger
 *   - chevron toggle ▼
 *
 * Sécurité (CRITIQUE) :
 *   - **Aucun `console.log`** du payload, du file_name, de la signed URL,
 *     du storage_path. Test RTL anti-leak vérifie 0 occurrence.
 *   - `payload.consent.ip_hash` : NON affiché (décision PO §7).
 *   - signed URL : `window.open(url, "_blank", "noopener,noreferrer")` —
 *     pas de stockage en state long, pas de localStorage, pas de cookie.
 *   - `useEventDocumentLinks` activé seulement quand expanded (lazy).
 *
 * a11y :
 *   - <article> avec <button> header pour toggle (jamais <div onClick>).
 *   - aria-expanded + aria-controls pointe vers les détails.
 *   - focus-visible:ring-2 brand-500.
 *   - touch-manipulation ≥ 44pt.
 */

import type {
  DocumentLinkRow,
  IntakeEventBrokerSanitizedRow,
} from "@kollectivia/shared";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiException } from "../../../lib/api";
import {
  getApiErrorMessage,
  normalizeLang,
} from "../../../lib/apiErrorMessage";
import { useFileDownloadUrl } from "../../../lib/queries/files";
import { useEventDocumentLinks } from "../../../lib/queries/intakeLinks";

import { IntakeEventStatusBadge } from "./IntakeEventStatusBadge";
import { RejectEventDialog } from "./RejectEventDialog";
import { ResolveEventDialog } from "./ResolveEventDialog";

type EventPayload = {
  consent?: {
    accepted?: boolean;
    timestamp?: string;
    ip_hash?: unknown; // décision PO §7 — non affiché en UI
  };
  comment?: string | null;
  email?: string | null;
  source_meta?: {
    channel?: string;
    ua_present?: boolean;
  };
  files?: Array<{ name?: string; mime?: string; size?: number }>;
};

type Props = {
  // D-NEW-C — typage broker-sanitized (consent.ip_hash + files[].sha256/
  // storage_bucket/storage_path strippés wire-side). Le frontend ne
  // consommait déjà pas ces champs (ip_hash typé `unknown` non rendu DOM,
  // storage_path absent du type local) — le swap est donc shape-compatible.
  event: IntakeEventBrokerSanitizedRow;
  /**
   * UUID intake_link parent — propagé par `IntakeLinkDetailPage` (9.G.2).
   * Requis pour les mutations resolve/reject (invalidation ciblée Q10=B :
   * `["intake-events","by-link"]` + `["intake-links","detail",linkId]`).
   */
  linkId: string;
};

export function IntakeEventCard({ event, linkId }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const [expanded, setExpanded] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const detailsId = useId();

  // Cast safe — payload est typé Record<string, unknown> côté shared.
  const payload = (event.payload ?? {}) as EventPayload;
  const consent = payload.consent;
  const comment = (payload.comment ?? "").trim();
  const email = (payload.email ?? "").trim();
  const filesFromPayload = payload.files ?? [];
  const previewComment =
    comment.length > 80 ? `${comment.slice(0, 80).trimEnd()}…` : comment;

  // Document links (lazy — enabled seulement si expanded).
  const docLinks = useEventDocumentLinks(event.id, { enabled: expanded });
  const fileCount = docLinks.data?.items.length ?? filesFromPayload.length;

  // 9.G.2 — boutons Resolve/Reject visibles uniquement si event en statut
  // terminal ouvert (Q3=A : visibles sur card collapsed + expanded).
  // Backend transitions autorisées : `pending_classification | pending_resolution → resolved | rejected`.
  const canAct =
    event.status === "pending_classification" ||
    event.status === "pending_resolution";

  return (
    <article className="rounded-xl border border-slate-200 bg-white shadow-sm transition-colors">
      <div className="flex w-full items-start gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={detailsId}
          aria-label={
            expanded
              ? t("intake_links.detail.events.card.collapse_aria")
              : t("intake_links.detail.events.card.expand_aria")
          }
          className="-m-2 flex min-w-0 flex-1 items-start gap-3 rounded-lg p-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 touch-manipulation"
        >
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 tabular-nums">
              <time dateTime={event.created_at}>
                {formatDateShort(event.created_at, lang)}
              </time>
              <IntakeEventStatusBadge status={event.status} />
              {event.language_detected && (
                <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium uppercase text-slate-600">
                  {event.language_detected}
                </span>
              )}
            </div>
            {previewComment ? (
              <p className="truncate text-sm text-slate-700">{previewComment}</p>
            ) : (
              <p className="truncate text-sm text-slate-400">
                {t("intake_links.detail.events.card.comment_empty")}
              </p>
            )}
            <p className="text-xs text-slate-500">
              {fileCount === 1
                ? t("intake_links.detail.events.card.files_count_one", {
                    count: fileCount,
                  })
                : t("intake_links.detail.events.card.files_count_other", {
                    count: fileCount,
                  })}
            </p>
          </div>
          <Chevron expanded={expanded} />
        </button>

        {/* 9.G.2 — boutons Resolve/Reject gated sur status (Q3=A toujours visibles
            collapsed + expanded si pending_*). Buttons hors du <button> toggle
            pour éviter nested <button> invalide ; alignés à droite via flex parent. */}
        {canAct && (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setResolveOpen(true)}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 touch-manipulation"
            >
              {t("intake_links.detail.events.actions.resolve.button")}
            </button>
            <button
              type="button"
              onClick={() => setRejectOpen(true)}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 touch-manipulation"
            >
              {t("intake_links.detail.events.actions.reject.button")}
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div
          id={detailsId}
          className="space-y-4 border-t border-slate-100 px-4 py-4 motion-safe:animate-fade-in"
        >
          {/* 9.G.2 — résolution / rejet détails si status terminal */}
          {event.status === "resolved" && (
            <Field label={t("intake_links.detail.events.actions.resolved_info_label")}>
              {event.resolved_project_id ? (
                <Link
                  to={`/projects/${event.resolved_project_id}`}
                  className="rounded-sm text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                >
                  {t("intake_links.detail.events.actions.resolved_to_project")}
                </Link>
              ) : event.resolved_quote_request_id ? (
                <span className="text-slate-700">
                  {t("intake_links.detail.events.actions.resolved_to_quote_request")}
                </span>
              ) : (
                <span className="text-slate-400">—</span>
              )}
              {event.resolved_at && (
                <span className="ml-2 text-xs text-slate-500 tabular-nums">
                  {formatDateLong(event.resolved_at, lang)}
                </span>
              )}
            </Field>
          )}

          {event.status === "rejected" && event.rejection_reason && (
            <Field label={t("intake_links.detail.events.actions.rejection_reason_label")}>
              <p className="whitespace-pre-line text-slate-700">
                {event.rejection_reason}
              </p>
            </Field>
          )}

          {/* Consent (sans ip_hash, décision PO §7) */}
          <Field label={t("intake_links.detail.events.card.consent_label")}>
            <span className="inline-flex items-center gap-1.5 text-emerald-700">
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 8.5l3 3 7-7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {consent?.timestamp
                ? t("intake_links.detail.events.card.consent_accepted_at", {
                    date: formatDateLong(consent.timestamp, lang),
                  })
                : t("intake_links.detail.events.card.consent_no_timestamp")}
            </span>
          </Field>

          {/* Received at (date complète) */}
          <Field label={t("intake_links.detail.events.card.received_at")}>
            <time
              dateTime={event.created_at}
              className="text-slate-700 tabular-nums"
            >
              {formatDateLong(event.created_at, lang)}
            </time>
          </Field>

          {/* Comment full */}
          <Field label={t("intake_links.detail.events.card.comment_label")}>
            {comment ? (
              <p className="whitespace-pre-line text-slate-700">{comment}</p>
            ) : (
              <span className="text-slate-400">
                {t("intake_links.detail.events.card.comment_empty")}
              </span>
            )}
          </Field>

          {/* Email */}
          <Field label={t("intake_links.detail.events.card.email_label")}>
            {email ? (
              <a
                href={`mailto:${email}`}
                className="text-brand-700 hover:underline"
              >
                {email}
              </a>
            ) : (
              <span className="text-slate-400">
                {t("intake_links.detail.events.card.email_empty")}
              </span>
            )}
          </Field>

          {/* Files */}
          <div>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
              {t("intake_links.detail.events.card.files_section")}
            </h3>
            <FilesList docLinks={docLinks} />
          </div>
        </div>
      )}

      {/* 9.G.2 — dialogs resolve/reject montés au niveau card. Render conditionnel
          interne du dialog (return null si !open) — pas de coût render si fermé. */}
      <ResolveEventDialog
        open={resolveOpen}
        eventId={event.id}
        linkId={linkId}
        onClose={() => setResolveOpen(false)}
        onResolved={() => setResolveOpen(false)}
      />
      <RejectEventDialog
        open={rejectOpen}
        eventId={event.id}
        linkId={linkId}
        onClose={() => setRejectOpen(false)}
        onRejected={() => setRejectOpen(false)}
      />
    </article>
  );
}

// ---------------------------------------------------------------------------
// Sous-composants
// ---------------------------------------------------------------------------

function Chevron({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className={`mt-1 shrink-0 text-slate-400 transition-transform motion-reduce:transition-none ${
        expanded ? "rotate-90" : ""
      }`}
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

function FilesList({
  docLinks,
}: {
  docLinks: ReturnType<typeof useEventDocumentLinks>;
}) {
  const { t } = useTranslation();

  if (docLinks.isLoading && !docLinks.data) {
    return (
      <p className="text-sm text-slate-400">
        {t("intake_links.detail.events.card.files_loading")}
      </p>
    );
  }

  const items = docLinks.data?.items ?? [];
  if (items.length === 0) {
    return (
      <p className="text-sm text-slate-400">
        {t("intake_links.detail.events.card.files_empty")}
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {items.map((dl) => (
        <li key={dl.id}>
          <FileRowItem docLink={dl} />
        </li>
      ))}
    </ul>
  );
}

function FileRowItem({ docLink }: { docLink: DocumentLinkRow }) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const downloadFile = useFileDownloadUrl();

  const apiErrorMsg =
    downloadFile.error instanceof ApiException
      ? getApiErrorMessage(downloadFile.error, lang) ||
        t("intake_links.detail.events.card.download_failed")
      : downloadFile.isError
        ? t("intake_links.detail.events.card.download_failed")
        : null;

  const onDownload = async () => {
    try {
      const result = await downloadFile.mutateAsync(docLink.file_id);
      // Anti-leak strict : NE PAS console.log la signed URL.
      // window.open avec noopener pour éviter de partager `window.opener`.
      if (typeof window !== "undefined") {
        window.open(result.url, "_blank", "noopener,noreferrer");
      }
    } catch {
      /* erreur affichée via apiErrorMsg ci-dessus, pas de console */
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-800">
          {docLink.display_name ?? "—"}
        </p>
        {apiErrorMsg && (
          <p className="mt-0.5 text-xs text-red-700" role="alert">
            {apiErrorMsg}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => void onDownload()}
        disabled={downloadFile.isPending}
        aria-busy={downloadFile.isPending}
        className="shrink-0 rounded-md border border-brand-600 bg-white px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-60 touch-manipulation"
      >
        {t("intake_links.detail.events.card.download")}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers — date formatters (Intl.DateTimeFormat natif)
// ---------------------------------------------------------------------------

function formatDateShort(iso: string, lang: "fr" | "en"): string {
  try {
    return new Intl.DateTimeFormat(lang === "fr" ? "fr-CA" : "en-CA", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function formatDateLong(iso: string, lang: "fr" | "en"): string {
  try {
    return new Intl.DateTimeFormat(lang === "fr" ? "fr-CA" : "en-CA", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
